import TextWithImageInput from 'components/TextWithImageInput';
import { Panel } from './Panel';
import { Button, Image, Modal } from 'antd';
import { EyeOutlined, FormOutlined, PlusOutlined } from '@ant-design/icons';
import { useState, useMemo, useRef, useEffect } from 'react';
import {
  convertMessageToTextAndImage,
  convertTextAndImageToMessage,
} from 'views/Conversation/Sider/ToDoBundle/helper';
import CustomUpload, { CustomUploadRef } from 'views/Conversation/Sider/ToDoBundle/Upload';
import { useResultTypeTipsLabel } from 'views/Todo/hooks/useLabel';
import { useUpdateTodoMutation } from 'hooks/business/chat/useTicketMutations';
import type { UseTicketListParams } from 'hooks/business/chat/useTicketMutations';

export interface NoteProps {
  ticket: Todo.TodoInstance;
  listParams: UseTicketListParams | null;
  conversationId: string;
  readonly?: boolean;
}

export const Note = (props: NoteProps) => {
  const { ticket, listParams, conversationId, readonly = false } = props;
  const { t } = useResultTypeTipsLabel();
  const { id, description, platform, storeId } = ticket;

  const [visible, setVisible] = useState(false);
  const [editText, setEditText] = useState('');
  const [editImages, setEditImages] = useState<string[]>([]);
  const customUploadRef = useRef<CustomUploadRef>(null);

  const { text, images } = useMemo(
    () => convertMessageToTextAndImage(description ?? []),
    [description],
  );

  const updateTodoMutation = useUpdateTodoMutation(listParams);

  // 打开弹窗时用当前 ticket 的 description 初始化编辑态
  useEffect(() => {
    if (visible) {
      setEditText(text);
      setEditImages(images);
    }
  }, [visible, text, images]);

  const handleOk = () => {
    updateTodoMutation.mutate(
      {
        id,
        description: convertTextAndImageToMessage(editText, editImages),
      },
      {
        onSuccess: () => setVisible(false),
      },
    );
  };

  const hasContent = text || images?.length > 0;

  const mainContent = (
    <div className="p-3 bg-[var(--fett-bg-subtle)] rounded">
      {text ? (
        <div className="text-[var(--fett-text-primary)] whitespace-pre-wrap break-words">
          {text}
        </div>
      ) : null}
      {images?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {images.map((url, idx) => (
            <Image
              key={idx}
              src={url}
              alt=""
              className="object-cover rounded max-h-12"
              preview={{
                mask: <EyeOutlined />,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (readonly) {
    if (hasContent) {
      return <Panel title={t('备注')}>{mainContent}</Panel>;
    }
    return null;
  }

  return (
    <Panel
      title={t('备注')}
      titleExtra={
        <Button
          type="link"
          className="!px-0"
          icon={hasContent ? <FormOutlined /> : <PlusOutlined />}
          onClick={() => setVisible(true)}
        >
          {hasContent ? t('编辑') : t('添加')}
        </Button>
      }
    >
      {hasContent ? mainContent : null}
      <Modal
        title={hasContent ? t('编辑') : t('添加')}
        visible={visible}
        onCancel={() => setVisible(false)}
        onOk={handleOk}
        confirmLoading={updateTodoMutation.isLoading}
        destroyOnClose
      >
        <TextWithImageInput
          text={editText}
          images={editImages}
          onTextChange={setEditText}
          onImagesChange={setEditImages}
          placeholder={t('trade.请输入')}
          disabled={readonly}
          renderImageUpload={({ value, onChange, disabled: uploadDisabled }) => (
            <CustomUpload
              ref={customUploadRef}
              disabled={uploadDisabled}
              selfHosted
              platform={platform}
              internalStoreId={storeId}
              conversationId={conversationId}
              onChange={onChange}
              value={value}
              direct={false}
            />
          )}
          onPasteImage={(file) => customUploadRef.current?.handUpload(file)}
        />
      </Modal>
    </Panel>
  );
};
